import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";

import { InputLabel } from "../../../../components/inputs/InputLabel";
import { ReminderActionResult } from "../../../../hooks/useProspectReminders";
import { snoozeBounds, toIsoDate } from "../../../../utils/reminders";

// The backend's limit (400 INVALID_FOLLOW_UP_CALL beyond it).
export const MAX_CALL_NOTE = 2000;

interface LogCallDialogProps {
  visible: boolean;
  onHide: () => void;
  onSubmit: (note: string, snoozeUntil: string | null) => Promise<ReminderActionResult>;
}

/**
 * CWA-189: record a follow-up call on a stalled onboarding. Clears "Needs
 * follow-up", puts the note in the edit history, and can snooze the reminders
 * in the same step (e.g. the customer is away until a date).
 *
 * Not the contract tab's "Log reminder" (LogReminderDialog), which records a
 * contract chase on the contract timeline.
 */
const LogCallDialog: React.FC<LogCallDialogProps> = ({ visible, onHide, onSubmit }) => {
  const [note, setNote] = useState("");
  const [date, setDate] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { min, max } = snoozeBounds();

  useEffect(() => {
    if (!visible) {
      setNote("");
      setDate(null);
      setError(null);
      setSubmitting(false);
    }
  }, [visible]);

  const trimmed = note.trim();
  const tooLong = trimmed.length > MAX_CALL_NOTE;

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    const result = await onSubmit(trimmed, date ? toIsoDate(date) : null);
    setSubmitting(false);
    if (result.ok) onHide();
    else setError(result.message ?? null);
  };

  const footer = (
    <div>
      <Button label="Cancel" text onClick={onHide} disabled={submitting} />
      <Button
        label="Log call"
        icon="pi pi-phone"
        onClick={submit}
        loading={submitting}
        disabled={submitting || !trimmed || tooLong}
      />
    </div>
  );

  return (
    <Dialog header="Log follow-up call" visible={visible} onHide={onHide} footer={footer} style={{ width: "32rem" }}>
      {error && <Message severity="error" className="w-full mb-3" text={error} />}
      <p className="mt-0">
        Record what you agreed with the customer. This clears "Needs follow-up"; the reminders carry on unless you
        snooze them.
      </p>
      <div className="field">
        <InputLabel name="follow_up_note" label="Note" required />
        <InputTextarea
          id="follow_up_note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={4}
          className="w-full"
        />
        <small className={tooLong ? "p-error" : "text-500"}>
          {trimmed.length} / {MAX_CALL_NOTE}
        </small>
      </div>
      <div className="field">
        <InputLabel name="follow_up_snooze" label="Snooze reminders until (optional)" />
        <Calendar
          inputId="follow_up_snooze"
          aria-label="Snooze reminders until"
          value={date}
          onChange={(e) => setDate((e.value as Date) ?? null)}
          minDate={min}
          maxDate={max}
          dateFormat="dd/mm/yy"
          showIcon
          showButtonBar
          className="w-full"
        />
      </div>
    </Dialog>
  );
};

export default LogCallDialog;
